import "./ProjectSelection.css";

import { Dispatch, FormEvent, SetStateAction, useMemo } from "react";

import { ProjectDatesConstants } from "../../../../../../../../../constants";
import { ProjectDatesData, SelectData } from "../../../../../../../../../models";
import { ServiceError } from "../../../../../../../../../service/Shared";
import {
  DesignSection,
  FeasibilitySection,
  ImplementationSection,
  OngoingMonitoringSection,
} from "../../../../../../../../../templates/EditDatesPanel/components";
import {
  getProjectDateValidationErrorFieldKeys,
  hasProjectDateValidationError,
} from "../../../../../../../../../utils";
import {
  ActionButtons,
  Autocomplete,
  Button,
  Column,
  Divider,
  ErrorBox,
  ReadonlyText,
  Row,
} from "../../../../../../../../../widget";
import { useProjectSelection } from "./useProjectSelection";

interface ProjectSelectionProps {
  errors: ServiceError[] | undefined;
  defaultProjectUuid?: string;

  projectsList: SelectData;
  selectedProject: ProjectDatesData | undefined;
  setSelectedProject: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  handleClearSelection: () => void;
  onProjectsSearch: (search: string) => Promise<void>;

  isHandleSubmitProjectPhaseDatesLoading: boolean;
  handleSubmitProjectPhaseDatesForm: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleClose: () => void;
}

export const ProjectSelection = ({
  errors,
  defaultProjectUuid,
  projectsList,
  selectedProject,
  setSelectedProject,
  handleClearSelection,
  onProjectsSearch,

  isHandleSubmitProjectPhaseDatesLoading,
  handleSubmitProjectPhaseDatesForm,
  handleClose,
}: ProjectSelectionProps): JSX.Element => {
  const {
    dateSchema,
    addedCycleCount,
    handleSelectProject,
    handleUpdateProjectDates,
    handleAddCycle,
    handleRemoveCycle,
  } = useProjectSelection({
    defaultProjectUuid,
    selectedProject,
    setSelectedProject,
  });
  const errorFieldKeys = useMemo(() => getProjectDateValidationErrorFieldKeys(errors), [errors]);
  const hasDateError = (key: string): boolean => hasProjectDateValidationError(errorFieldKeys, key);

  return (
    <div className="ProjectSelection">
      <Row spacingV="xl">
        <Column span={4}>
          {defaultProjectUuid && selectedProject ? (
            <ReadonlyText label="Project" value={selectedProject?.displayName} />
          ) : (
            <div className="ProjectSelectionControls">
              <div className="ProjectSelectionAutocomplete">
                <Autocomplete
                  label="Search project"
                  placeholder="Start typing to find available project"
                  initialKey={selectedProject?.uuid}
                  data={projectsList}
                  onSelect={handleSelectProject}
                  onTypeSearch={onProjectsSearch}
                />
              </div>
              {selectedProject && (
                <Button
                  text="Clear"
                  variant="tertiary"
                  onClick={handleClearSelection}
                  corners="rounded"
                  size="small"
                  className="ApplyWorkflowTemplatePanelClearButton"
                />
              )}
            </div>
          )}
        </Column>
      </Row>
      <form onSubmit={(e) => handleSubmitProjectPhaseDatesForm(e)}>
        {selectedProject && (
          <>
            <Row spacingV="xl">
              <Column span={12}>
                <Divider type="horizontal" />
              </Column>
            </Row>
            <Row>
              <Column span={12}>
                <Row spacingV={errors && errors.length > 0 ? "ml" : "xl"}>
                  <Column span={12}>
                    <h2>Phase dates</h2>
                  </Column>
                </Row>
                {errors && errors.length > 0 && (
                  <Row spacingV="ml">
                    <Column span={6}>
                      <ErrorBox text={errors.map((error) => error.message)} />
                    </Column>
                  </Row>
                )}
                <FeasibilitySection
                  dates={selectedProject.dates}
                  handleUpdateProjectDates={handleUpdateProjectDates}
                  hasDateError={hasDateError}
                  schema={dateSchema}
                />
                <Row spacingV="xl">
                  <Column span={12}>
                    <Divider type="horizontal" />
                  </Column>
                </Row>
                <DesignSection
                  dates={selectedProject.dates}
                  handleUpdateProjectDates={handleUpdateProjectDates}
                  hasDateError={hasDateError}
                  schema={dateSchema}
                />
                <Row spacingV="xl">
                  <Column span={12}>
                    <Divider type="horizontal" />
                  </Column>
                </Row>
                <ImplementationSection
                  dates={selectedProject.dates}
                  handleUpdateProjectDates={handleUpdateProjectDates}
                  hasDateError={hasDateError}
                  schema={dateSchema}
                />
                <Row spacingV="xl">
                  <Column span={12}>
                    <Divider type="horizontal" />
                  </Column>
                </Row>
                <OngoingMonitoringSection
                  addedCycleCount={addedCycleCount}
                  subsequentFrequencySelectData={
                    ProjectDatesConstants.ONGOING_MONITORING_SUBSEQUENT_FREQUENCY_SELECT_DATA
                  }
                  dates={selectedProject.dates}
                  handleUpdateProjectDates={handleUpdateProjectDates}
                  hasDateError={hasDateError}
                  handleAddCycle={handleAddCycle}
                  handleRemoveCycle={handleRemoveCycle}
                  schema={dateSchema}
                />
              </Column>
            </Row>
          </>
        )}
        <Row spacingV="xl">
          <Column span={12}>
            <ActionButtons
              disabled={!selectedProject}
              submitButtonLabel="Next"
              onCancel={handleClose}
              isLoading={isHandleSubmitProjectPhaseDatesLoading}
            />
          </Column>
        </Row>
      </form>
    </div>
  );
};
